import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import Swal from 'sweetalert2';
import { ApiService } from './api.service';
import { finalize, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class JobdescriptionparsingService {

  jobPostForm : UntypedFormGroup;
  engagementTypeList : any[];
  payTypeList: any[];
  countryList: any[];
  years: any[];
  Toyears: any[];
  years2: any[];
  durationTypeList: any[];
  usStatesList: any[];
  stateListCA: any[];
  stateListIN: any[];
  stateListAU: any[];

  // JD Parsing state variables
  isJdParsing = false;
  jdParseSuccess = false;
  jdParseFailed = false;
  showParseHint = false;

  // Emits parsed skills so components can update their ng-select bindings
  parsedSkills$ = new Subject<{ primarySkills: string[]; secondarySkills: string[] }>();

  constructor(private API : ApiService) { }

  get jobControls() {
    return this.jobPostForm?.controls || {};
  }

  patchFormToService(
    form: UntypedFormGroup,
    config: {
      engagementTypeList: any[];
      payTypeList: any[];
      countryList: any[];
      years: number[];
      Toyears: number[];
      years2: number[];
      durationTypeList: any[];
      usStatesList: any[];
      stateListCA: any[];
      stateListIN: any[];
      stateListAU: any[];
    }
  ): void {
    this.jobPostForm = form;
  
    this.engagementTypeList = config.engagementTypeList || [];
    this.payTypeList = config.payTypeList || [];
    this.countryList = config.countryList || [];
    this.years = config.years || [];
    this.Toyears = config.Toyears || [];
    this.years2 = config.years2 || [];
    this.durationTypeList = config.durationTypeList || [];
    this.usStatesList = config.usStatesList || [];
    this.stateListCA = config.stateListCA || [];
    this.stateListIN = config.stateListIN || [];
    this.stateListAU = config.stateListAU || [];
  }
  

  /**
   * sample job form
   * | #   | FormControl Name  |
  |-----|-------------------|
  | 1   | jobId             |*
  | 2   | jobPostedBehalfOf |
  | 3   | clientType        |
  | 4   | showClientType    |
  | 5   | clientName        |
  | 6   | showClientName    |
  | 7   | jobClassification | get from parsing*
  | 8   | jobTitle          | get from parsing*
  | 9   | primarySkills     | get from parsing*
  | 10  | secondarySkills   | get from parsing*
  | 11  | country           | get from parsing
  | 12  | state             | get from parsing
  | 13  | city              | get from parsing
  | 14  | zipCode           | get from parsing
  | 15  | duration          | get from parsing
  | 16  | durationType      | get from parsing
  | 17  | experienceFrom    | get from parsing
  | 18  | experienceTo      | get from parsing
  | 19  | payType           | get from parsing
  | 20  | workMode          | get from parsing
  | 21  | targetRateFrom    | get from parsing
  | 22  | targetRateTo      | get from parsing
  | 23  | selectedNetworks  | 
  | 24  | securityClearance |
  | 25  | jobDescription    | *
  rawdata
  response
  futurellmresponse
   */  

  parseJobDescription(): void {
    const description: string = this.jobControls?.jobDescription?.value?.trim();
    const jobId: string = this.jobControls?.jobId?.value?.trim();
    if (!description || !jobId) {
      return;
    }

    // Check minimum character length for parsing
    if (description.length < 50) {
      Swal.fire({
        icon: 'info',
        title: 'Not Enough Content',
        text: 'Please enter at least 50 characters in the job description to enable parsing.',
        timer: 3000,
        showConfirmButton: false
      });
      return;
    }

    const payload = {
      jobDescription: description,
      jobId: jobId
    };

    // Set parsing state
    this.isJdParsing = true;
    this.jdParseSuccess = false;
    this.jdParseFailed = false;
    this.showParseHint = false;

    this.API.create('jobs/parseJobDescription', payload)
      .pipe(finalize(() => {
        this.isJdParsing = false;
      }))
      .subscribe({
        next: (res: any) => {
          // reset retry counter on success
          this.retryCount = 0;

          if (!res?.data?.parsedDescription) {
            this.jdParseFailed = true;
            this.handleParseFailure();
            return;
          }

          const data = res.data.parsedDescription;
          this.patchIfPresent('jobClassification', data.jobClassification);
          this.patchIfPresent('jobTitle', data.jobTitle);
          this.patchIfPresent('primarySkills', data.primarySkills);
          this.patchIfPresent('secondarySkills', data.secondarySkills);

          // Notify components to update their ng-select bindings
          const primarySkills = Array.isArray(data.primarySkills) ? data.primarySkills.filter(Boolean) : [];
          const secondarySkills = Array.isArray(data.secondarySkills) ? data.secondarySkills.filter(Boolean) : [];
          this.parsedSkills$.next({ primarySkills, secondarySkills });
          this.patchIfPresent('country', data.country);
          this.patchIfPresent('state', data.state);
          this.patchIfPresent('city', data.city);
          this.patchIfPresent('zipCode', data.zipCode);
          this.patchIfPresent('duration', data.duration);
          this.patchIfPresent('durationType', data.durationType);
          this.patchIfPresent('experienceFrom', data.experienceFrom);
          this.patchIfPresent('experienceTo', data.experienceTo);
          this.patchIfPresent('payType', data.payType);
          this.patchIfPresent('workMode', data.workMode);
          this.patchIfPresent('targetRateFrom', data.targetRateFrom);
          this.patchIfPresent('targetRateTo', data.targetRateTo);

          // Update jobDescription Summernote with structured JD HTML
          const structuredHtml = this.buildStructuredJobHtml(data);
          if (structuredHtml) {
            this.jobPostForm.get('jobDescription')?.patchValue(structuredHtml);
            setTimeout(() => {
              if ((window as any).$ && (window as any).$('#jobDescription').length > 0) {
                (window as any).$('#jobDescription').summernote('code', structuredHtml);
              }
            }, 200);
          }

          this.jobPostForm.markAllAsTouched();
          this.jdParseSuccess = true;
        },
        error: () => {
          this.jdParseFailed = true;
          this.handleParseFailure();
        }
      }
    );
  }
  

  workmodes = {
    onsite : 'Onsite',
    remote : 'Remote',
    hybrid : 'Hybrid'
  }

  private buildSummaryParagraph(data: any): string {
    const title          = data.jobTitle          ? `<strong>${data.jobTitle}</strong>`    : 'this role';
    const classification = data.jobClassification || '';
    const workMode       = data.workMode           || '';

    const locationParts  = [data.city, data.state, data.country].filter(Boolean);
    const location       = locationParts.join(', ');

    const rateFrom       = data.targetRateFrom;
    const rateTo         = data.targetRateTo;
    const payType        = data.payType || '';
    const pay            = (rateFrom || rateTo)
      ? `${rateFrom ?? ''}${rateTo ? ' – ' + rateTo : ''} ${payType}`.trim()
      : '';

    const duration       = (data.duration || data.durationType)
      ? [data.duration, data.durationType].filter(Boolean).join(' ')
      : '';

    const expFrom        = data.experienceFrom;
    const expTo          = data.experienceTo;

    const primarySkills: string[]   = Array.isArray(data.primarySkills)   ? data.primarySkills.filter(Boolean)   : [];
    const secondarySkills: string[] = Array.isArray(data.secondarySkills) ? data.secondarySkills.filter(Boolean) : [];

    const sentences: string[] = [];

    // Sentence 1 — opening
    let s1 = `We are seeking a detail-oriented and results-driven ${title}`;
    if (classification) s1 += ` for a <strong>${classification}</strong> engagement`;
    if (workMode)       s1 += `${classification ? ',' : ''} offering a <strong>${workMode}</strong> work arrangement`;
    s1 += '.';
    sentences.push(s1);

    // Sentence 2 — location / pay / duration
    const s2Parts: string[] = [];
    if (location) s2Parts.push(`based in <strong>${location}</strong>`);
    if (pay)      s2Parts.push(`with a pay rate of <strong>${pay}</strong>`);
    if (duration) s2Parts.push(`for a duration of <strong>${duration}</strong>`);
    if (s2Parts.length) sentences.push('This position is ' + s2Parts.join(', ') + '.');

    // Sentence 3 — experience + primary skills (tools/technologies per backend definition)
    if (expFrom || expTo || primarySkills.length) {
      let s3 = 'The ideal candidate';
      if (expFrom || expTo) {
        const exp = `${expFrom ?? '0'}${expTo ? ' – ' + expTo : '+'} years`;
        s3 += ` brings <strong>${exp}</strong> of hands-on experience`;
      }
      if (primarySkills.length) {
        const listed = primarySkills.slice(0, 4).join(', ');
        const more   = primarySkills.length > 4 ? ` and ${primarySkills.length - 4} more` : '';
        s3 += `${expFrom || expTo ? ' and is' : ' is'} proficient in industry-leading tools and technologies such as <strong>${listed}${more}</strong>`;
      }
      sentences.push(s3 + '.');
    }

    // Sentence 4 — secondary skills (methodologies/practices per backend definition)
    if (secondarySkills.length) {
      const listed = secondarySkills.slice(0, 5).join(', ');
      const more   = secondarySkills.length > 5 ? ` and more` : '';
      sentences.push(
        `Strong domain knowledge in key practices including <strong>${listed}${more}</strong> is essential for this role.`
      );
    }

    // Sentence 5 — closing
    sentences.push(
      'This role requires strong ability to collaborate with cross-functional teams, identify defects early in the development cycle, and ensure high-quality product delivery.'
    );

    return sentences.length ? `<p>${sentences.join(' ')}</p>` : '';
  }

  private buildStructuredJobHtml(data: any): string {
    let html = '';

    // ── Header ──────────────────────────────────────────────────────────────
    if (data.jobTitle) {
      html += `<p><strong>Job Title:</strong> ${data.jobTitle}</p>`;
    }

    // ── About the Role ───────────────────────────────────────────────────────
    html += `<p><strong>About the Role</strong></p>`;

    // Show extracted summary if backend returned one
    if (data.aboutRole || data.jobSummary) {
      html += `<p>${data.aboutRole || data.jobSummary}</p>`;
    }

    // Always append auto-generated paragraph for extra context
    const generatedSummary = this.buildSummaryParagraph(data);
    if (generatedSummary) {
      html += generatedSummary;
    }

    // ── Job Details ──────────────────────────────────────────────────────────
    const detailRows: string[] = [];

    if (data.jobClassification) {
      detailRows.push(`<p><strong>Job Classification:</strong> ${data.jobClassification}</p>`);
    }
    if (data.workMode) {
      detailRows.push(`<p><strong>Work Mode:</strong> ${data.workMode}</p>`);
    }
    if (data.experienceFrom || data.experienceTo) {
      const from = data.experienceFrom ?? '0';
      const to   = data.experienceTo   ?? '';
      detailRows.push(`<p><strong>Experience Required:</strong> ${from}${to ? ' – ' + to : '+'} years</p>`);
    }
    const locationParts = [data.city, data.state, data.country].filter(Boolean);
    if (locationParts.length > 0) {
      detailRows.push(`<p><strong>Location:</strong> ${locationParts.join(', ')}</p>`);
    }
    if (data.duration || data.durationType) {
      detailRows.push(`<p><strong>Duration:</strong> ${[data.duration, data.durationType].filter(Boolean).join(' ')}</p>`);
    }
    if (data.targetRateFrom || data.targetRateTo) {
      const from    = data.targetRateFrom ?? '';
      const to      = data.targetRateTo   ?? '';
      const payType = data.payType        ?? '';
      detailRows.push(`<p><strong>Pay Rate:</strong> ${from}${to ? ' – ' + to : ''} ${payType}</p>`);
    }
    if (data.educationRequirements) {
      detailRows.push(`<p><strong>Education:</strong> ${data.educationRequirements}</p>`);
    }
    if (data.securityClearance) {
      detailRows.push(`<p><strong>Security Clearance:</strong> ${data.securityClearance}</p>`);
    }

    if (detailRows.length > 0) {
      html += detailRows.join('');
    }

    // ── Primary Skills ───────────────────────────────────────────────────────
    if (data.primarySkills?.length > 0) {
      const list = Array.isArray(data.primarySkills) ? data.primarySkills.filter(Boolean) : [data.primarySkills];
      html += `<p><strong>Primary Skills</strong></p><ul>`;
      list.forEach((s: string) => html += `<li>${s}</li>`);
      html += `</ul>`;
    }

    // ── Secondary Skills ─────────────────────────────────────────────────────
    if (data.secondarySkills?.length > 0) {
      const list = Array.isArray(data.secondarySkills) ? data.secondarySkills.filter(Boolean) : [data.secondarySkills];
      html += `<p><strong>Secondary Skills</strong></p><ul>`;
      list.forEach((s: string) => html += `<li>${s}</li>`);
      html += `</ul>`;
    }

    // ── Soft Skills ──────────────────────────────────────────────────────────
    if (data.softSkills?.length > 0) {
      const list = Array.isArray(data.softSkills) ? data.softSkills.filter(Boolean) : [data.softSkills];
      html += `<p><strong>Soft Skills</strong></p><ul>`;
      list.forEach((s: string) => html += `<li>${s}</li>`);
      html += `</ul>`;
    }

    // ── Certifications ───────────────────────────────────────────────────────
    if (data.certifications?.length > 0) {
      const list = Array.isArray(data.certifications) ? data.certifications.filter(Boolean) : [data.certifications];
      html += `<p><strong>Certifications</strong></p><ul>`;
      list.forEach((c: string) => html += `<li>${c}</li>`);
      html += `</ul>`;
    }

    // ── Responsibilities ─────────────────────────────────────────────────────
    if (data.responsibilities?.length > 0) {
      html += `<p><strong>Responsibilities</strong></p><ul>`;
      data.responsibilities.filter(Boolean).forEach((r: string) => html += `<li>${r}</li>`);
      html += `</ul>`;
    }

    // ── Requirements ─────────────────────────────────────────────────────────
    if (data.requirements?.length > 0) {
      html += `<p><strong>Requirements</strong></p><ul>`;
      data.requirements.filter(Boolean).forEach((r: string) => html += `<li>${r}</li>`);
      html += `</ul>`;
    }

    // ── Nice to Have ─────────────────────────────────────────────────────────
    if (data.niceToHave?.length > 0) {
      html += `<p><strong>Nice to Have</strong></p><ul>`;
      data.niceToHave.filter(Boolean).forEach((r: string) => html += `<li>${r}</li>`);
      html += `</ul>`;
    }

    // ── Benefits ─────────────────────────────────────────────────────────────
    if (data.benefits?.length > 0) {
      const list = Array.isArray(data.benefits) ? data.benefits.filter(Boolean) : [data.benefits];
      html += `<p><strong>Benefits</strong></p><ul>`;
      list.forEach((b: string) => html += `<li>${b}</li>`);
      html += `</ul>`;
    }

    return html;
  }

  private patchIfPresent(controlName: string, value: any): void {
    const control = this.jobControls?.[controlName];
    if (!control || value == null || value === '') return;
  
    // Handle arrays (skills, etc.)
    if (Array.isArray(value)) {
      const clean = value
        .filter(v => v != null && v !== '')
        .map(v => typeof v === 'string' ? v.trim() : v);
  
      if (clean.length) {
        control.patchValue(clean, { emitEvent: false });
      }
      return;
    }
  
    switch (controlName) {
      case 'jobTitle':
        this.patchString(control, value);
        break;

      case 'jobClassification':
        this.patchStringFromList(control, value, this.engagementTypeList);
        break;
  
      case 'workMode':
        this.patchStringFromList(
          control,
          value,
          Object.values(this.workmodes)
        );
        break;
  
      case 'payType':
        this.patchStringFromList(control, value, this.payTypeList);
        break;
  
  
      case 'country':
        const code = this.countryList
          ?.find(c => c.countryName === value)?.countryCode;
        if (code) {
          control.patchValue(code, { emitEvent: false });
        }
        break;
  
      case 'experienceFrom':
        this.patchNumberFromList(control, value, this.years);
        break;
  
      case 'experienceTo':
        this.patchNumberFromList(control, value, this.Toyears);
        break;
  
      case 'duration':
        this.patchNumberFromList(control, value, this.years2);
        break;
  
      case 'durationType':
        this.patchStringFromList(control, value, this.durationTypeList);
        break;

      case 'targetRateFrom':
        control.patchValue(value);
        break;
  
      case 'targetRateTo':
        control.patchValue(value);
        break;
  
      case 'state':
        this.patchStateValue(control, value);
        break;
  
      case 'city':
        this.patchCity(control, value);
        break;
  
      case 'zipCode':
        this.patchZip(control, value);
        break;
  
      default:
        this.patchString(control, value);
    }
  }

  private patchString(control: any, value: any): void {
    if (typeof value === 'string' && value.trim()) {
      control.patchValue(value.trim());
    }
  }
  
  private patchNumberFromList(control: any, value: any, list: number[]): void {
    const num = Number(value);
    if (!isNaN(num) && list.includes(num)) {
      control.patchValue(num);
    }
  }
  
  private patchStringFromList(control: any, value: any, list: string[]): void {
    if (typeof value !== 'string') return;
  
    const match = list.find(
      v => v.toLowerCase() === value.toLowerCase()
    );
  
    if (match) {
      control.patchValue(match);
    }
  }
  
  private patchCity(control: any, value: any): void {
    if (typeof value === 'string') {
      const city = value.trim().replace(/\s+/g, ' ');
      if (city) {
        control.patchValue(city);
      }
    }
  }
  
  private patchZip(control: any, value: any): void {
    if (typeof value === 'string') {
      const zip = value
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9\- ]/g, '');
  
      if (zip) {
        control.patchValue(zip);
      }
    }
  }
  
  

  patchStateValue(control:any, value: string) {
    if (!value || typeof value !== 'string') {
      return;
    }
  
    const country = this.jobPostForm.value.country;
    const lowerValue = value.toLowerCase();
  
    let match: any = null;
  
    if (country === 'US') {
      match = this.usStatesList.find(
        s => s.toLowerCase() === lowerValue
      );
    }
    else if (country === 'CA') {
      match = this.stateListCA.find(
        s => s.stateName.toLowerCase() === lowerValue
      )?.stateName;
    }
    else if (country === 'IN') {
      match = this.stateListIN.find(
        s => s.stateName.toLowerCase() === lowerValue
      )?.stateName;
    }
    else if (country === 'AU') {
      match = this.stateListAU.find(
        s => s.stateName.toLowerCase() === lowerValue
      )?.stateName;
    }
    else {
      // Other countries → free text
      control.patchValue(value.trim());
      return;
    }
  
    if (match) {
      control.patchValue(match);
    }
  }

  private handleParseFailure(): void {
    // ---- Auto retry (first failure) ----
    if (this.retryCount === 0) {
      this.retryCount++;
      this.parseJobDescription();
      return;
    }
  
    // ---- Manual retry via Swal (second failure) ----
    if (this.retryCount < this.MAX_RETRIES) {
      this.retryCount++;
      this.confirmRetryWithSwal();
    }
  }
  

  private retryCount = 0;
  private readonly MAX_RETRIES = 2;
  private confirmRetryWithSwal(): void {
    Swal.fire({
      title: 'Parsing Failed',
      text: 'Unable to parse the job description. Do you want to retry?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Retry',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false
    }).then(result => {
      if (result.isConfirmed) {
        this.parseJobDescription();
      }
    });
  }
}
